import { identifierQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { buildPlaylistUrl } from "components/scrapEyeVideoPlayer/playlistUrl";
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import {
  useGetRadioSegmentInfoQuery,
  useGetRadioTranscriptQuery,
  useListRadioReceiversQuery,
} from "src/store/api/generatedApi";
import { useChannelName } from "./useChannelName";

// Fallback until the backend read resolves; the real value is operator-
// configurable per device (video_segment_seconds). The coverage query filters
// segments on an exact segment_length_sec and the live staleness cutoff derives
// from it, so both panels must share one fallback or they disagree about the
// same device.
export const DEFAULT_RADIO_SEGMENT_LENGTH_SEC = 15;

// Only the subscription options the panels actually pass. Typing this as the
// hook's full `Parameters<...>[1]` drags in RTK's `selectFromResult` generic,
// which makes the query result infer as `any` and strips the typing off `data`.
export const useRadioSegmentInfo = (
  deviceId: number,
  options?: {
    skip?: boolean;
    pollingInterval?: number;
    receiverId?: number | null;
  }
) => {
  const tenantName = useTenant();
  const { data } = useGetRadioSegmentInfoQuery(
    { tenantName, deviceId, receiverId: options?.receiverId ?? null },
    options
  );
  return {
    segmentLengthSec:
      data?.segment_length_sec ?? DEFAULT_RADIO_SEGMENT_LENGTH_SEC,
    lastReceivedAt: data?.last_received_at ?? null,
  };
};

// The channel a panel is playing. A device with several radio channels (a
// trunked fleet spreads its calls over a pool of them) offers one feed each, and
// the backend defaults to the most recently active — which is what the selection
// starts on, so a single-channel device behaves exactly as before.
export const useRadioReceivers = (deviceId: number) => {
  const tenantName = useTenant();
  const { data } = useListRadioReceiversQuery({ tenantName, deviceId });
  const receivers = data?.items ?? [];
  const [selectedId, setSelectedId] = useNavigationQuery(
    "radioReceiver",
    null,
    identifierQuery,
    "replace"
  );
  const selectedReceiverId =
    receivers.find((receiver) => receiver.id === selectedId)?.id ??
    receivers[0]?.id ??
    null;
  const channelName = useChannelName();
  const channelLabels = useMemo(
    () =>
      new Map(
        (data?.items ?? []).map((receiver) => [
          receiver.id,
          channelName(receiver),
        ])
      ),
    [data, channelName]
  );
  return {
    receivers,
    selectedReceiverId,
    selectReceiver: setSelectedId,
    channelLabels,
  };
};

// A window's transcript for one channel, or for every channel at once. The
// `all_receivers` flag has to win over `receiverId` on the wire, so the two are
// resolved here rather than at each call site.
export const useRadioTranscript = ({
  deviceId,
  receiverId,
  allChannels,
  startTime,
  endTime,
  skip,
}: {
  deviceId: number;
  receiverId: number | null;
  allChannels: boolean;
  startTime: string;
  endTime: string;
  skip: boolean;
}) => {
  const tenantName = useTenant();
  const { data } = useGetRadioTranscriptQuery(
    skip
      ? skipToken
      : {
          tenantName,
          deviceId,
          startTime,
          endTime,
          allReceivers: allChannels,
          receiverId: allChannels ? null : receiverId,
        }
  );
  return data;
};

export const useLiveRadioUrl = (
  deviceId: number | null,
  {
    segmentCount = 8,
    receiverId = null,
  }: { segmentCount?: number; receiverId?: number | null } = {}
) => {
  const tenantName = useTenant();

  return useMemo(() => {
    if (deviceId == null) return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/radio/device/${deviceId}/live.m3u8`,
      {
        segment_count: String(segmentCount),
        ...(receiverId == null ? {} : { receiver_id: String(receiverId) }),
      }
    );
  }, [tenantName, deviceId, segmentCount, receiverId]);
};

export const useHistoricalRadioUrl = (
  deviceId: number | null,
  startTime: string | null,
  endTime: string | null,
  receiverId: number | null = null
) => {
  const tenantName = useTenant();

  return useMemo(() => {
    if (
      deviceId == null ||
      startTime == null ||
      endTime == null ||
      startTime >= endTime
    )
      return null;
    return buildPlaylistUrl(
      `/api/v2/scrap_eyes/${tenantName}/radio/device/${deviceId}/playlist.m3u8`,
      {
        start_time: startTime,
        end_time: endTime,
        ...(receiverId == null ? {} : { receiver_id: String(receiverId) }),
      }
    );
  }, [tenantName, deviceId, startTime, endTime, receiverId]);
};
